import React, {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useRef,
} from 'react';
import c from 'classnames';

import Button from '../Button';

import './style.scss';

const ARROW_LEFT = 'ArrowLeft';
const ARROW_RIGHT = 'ArrowRight';
const ARROW_UP = 'ArrowUp';
const ARROW_DOWN = 'ArrowDown';
const ENTER = 'Enter';
const SPACEBAR = ' ';

export interface SwitchTab {
  key: string;
  label: string;
  icon?: string;
}

export interface SwitchProps {
  tabs: SwitchTab[];
  currentTab: string;
  idPrefix?: string;
  tabPanelRef?: RefObject<HTMLDivElement>;
  className?: string;
  onChange?: (key: string) => void;
}

const Switch: React.FC<SwitchProps> = (props) => {
  const {
    tabs,
    currentTab,
    idPrefix = 'tab',
    tabPanelRef,
    className,
    onChange,
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const classNames = c('Switch', className);
  const tabKeys = tabs.map((tab) => tab.key);
  const currentTabIndex = tabKeys.findIndex((tabKey) => tabKey === currentTab);
  const tabPanelId = tabPanelRef?.current?.id;

  function toggleTab(tabToSelectKey: string) {
    if (currentTabIndex < 0 || ref.current == null) {
      return;
    }

    const tabToSelect = ref.current.querySelector(`[data-switch-tab-key="${tabToSelectKey}"]`);

    if (!tabToSelect) {
      return;
    }

    (tabToSelect as HTMLElement).focus();

    if (onChange) {
      onChange(tabToSelectKey);
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    const { key } = event;

    if (currentTabIndex < 0 || ref.current == null) {
      return;
    }

    const isArrowKeys = [ARROW_LEFT, ARROW_UP, ARROW_RIGHT, ARROW_DOWN].includes(key);
    const isSpaceOrEnter = [SPACEBAR, ENTER].includes(key);

    if (!isArrowKeys && !isSpaceOrEnter) {
      return;
    }

    event.preventDefault();

    let direction = -1;

    if (isArrowKeys) {
      direction = key === ARROW_LEFT || key === ARROW_UP ? -1 : 1;
      // Reverse direction when shift key is pressed
      if (event.shiftKey) {
        direction = -direction;
      }
    }

    let newTabIndex = currentTabIndex + direction;
    if (isSpaceOrEnter) {
      // Calculate new index with circular navigation
      if (newTabIndex < 0) {
        newTabIndex = tabKeys.length - 1;
      } else if (newTabIndex >= tabKeys.length) {
        newTabIndex = 0;
      }
    }

    const tabToSelectKey = tabKeys[newTabIndex];

    if (tabToSelectKey == null) {
      return;
    }

    toggleTab(tabToSelectKey);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    const tabToSelectKey = event.currentTarget.dataset.switchTabKey;

    if (tabToSelectKey == null) {
      return;
    }

    toggleTab(tabToSelectKey);
  };

  return (
    <div className={classNames} role="tablist" ref={ref}>
      {tabs.map((tab) => {
        const isCurrentTab = currentTab === tab.key;

        return (
          <Button
            id={`${idPrefix}-${tab.key}`}
            role="tab"
            aria-selected={isCurrentTab}
            tabIndex={isCurrentTab ? 0 : -1}
            aria-controls={tabPanelId}
            data-switch-tab-key={tab.key}
            type={isCurrentTab ? 'primary' : 'secondary'}
            icon={tab.icon}
            iconPosition="left"
            onKeyDown={handleKeyDown}
            onClick={handleClick}
          >
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
};

export default Switch;
